/**
 * SoLineEditor (S2.2) — the SO create/edit line editor.
 *
 * - Product picker is GATED to Ready products (非產品就緒 products are ABSENT
 *   from the option list, never disabled); the empty-search state explains the
 *   readiness gate. Search matches internal SKU, names, Internal Product ID
 *   AND the selected customer's PN cross-refs (S2.4's mapping table shares
 *   the same product rows).
 * - Unit price is auto-filled from `resolvePrice` and rendered through the
 *   PriceTraceTrigger affordance (dotted underline + purple audit icon →
 *   shared PriceTracePanel drawer, S9.2 swap).
 * - Manual override goes through PriceOverrideModal → ReasonRequiredModal:
 *   a new price only ever lands together with a logged reason.
 * - Per-line margin = unitPrice − landedCost (Board D profit).
 */

import { useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  InputNumber,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  SyncOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FeatureGate } from '@/features/flags/FeatureGate';
import { canEditCostBasis } from '@/hooks/usePricing';
import { useConfirmModal } from '@/components/common/ConfirmModal';
import PurpleTag from '@/components/common/PurpleTag';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import type { Product } from '@/types/api/product';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import PriceOverrideModal from './PriceOverrideModal';
import PriceTraceTrigger from './PriceTraceTrigger';
import {
  draftLineBeyondThreshold,
  draftLineCostCurrency,
  draftLineLandedCost,
  draftLineMargin,
  draftLineMarginFloor,
  draftLineTotal,
} from './soDraftLine';
import type { DraftSoLine } from './soDraftLine';
import { formatAmount, formatPct } from './soFormat';
import { DismissibleTooltip } from './DismissibleTooltip';

const { Text } = Typography;

export interface SoLineEditorProps {
  lines: DraftSoLine[];
  /** Ready + active products ONLY (readiness gate lives in the feed). */
  products: Product[];
  productsLoading: boolean;
  customerId: number | null;
  currency: string;
  onAddLine: () => void;
  onRemoveLine: (key: string) => void;
  onSelectProduct: (key: string, product: Product) => void;
  onQtyChange: (key: string, qty: number) => void;
  onApplyOverride: (key: string, unitPrice: number, reason: string) => void;
  onRestorePrice: (key: string) => void;
  /**
   * S3.3 explicit re-resolve seat (DA-6) — invoked ONLY from the per-line
   * user-triggered action (confirm-gated when the line carries a manual
   * override: the modal names the override + its recorded reason and states
   * the §6.2 detach consequence). Never wired to an effect or onChange.
   * TRIPLE-gated (RULING-4): sales.orders route AND engines.pricing flag AND
   * canEditCostBasis() permission — all hide-don't-disable.
   */
  onReResolveAtQty: (key: string) => void;
  /**
   * Company minimum margin (percent of sell price) the page read from the
   * pricing store; null ⇒ no floor ⇒ no floor tag on any line. The margin
   * cell tags a line STRICTLY below it (same rule the handler applies on
   * save), so the operator sees the hold before creating the order.
   */
  minimumMarginPct: number | null;
}

interface PickerOption {
  value: string;
  label: string;
  partNumber?: string;
  searchText: string;
}

const SoLineEditor = ({
  lines,
  products,
  productsLoading,
  customerId,
  currency,
  onAddLine,
  onRemoveLine,
  onSelectProduct,
  onQtyChange,
  onApplyOverride,
  onRestorePrice,
  onReResolveAtQty,
  minimumMarginPct,
}: SoLineEditorProps) => {
  const { t } = useTranslation(['orders', 'common']);
  const { token } = theme.useToken();
  const { showConfirm } = useConfirmModal();
  // RULING-4 (2026-08-29): DA-7 manager gate extended to the re-resolve seat — hide, never disable.
  const reResolveSeat = canEditCostBasis();

  const [overrideKey, setOverrideKey] = useState<string | null>(null);
  const overrideLine = lines.find((line) => line.key === overrideKey) ?? null;

  // S3.3 — an overridden line confirms BEFORE the detach (modal names the
  // override value + recorded reason); a clean line re-resolves directly,
  // but still only on this explicit click.
  const handleReResolveClick = (line: DraftSoLine) => {
    if (line.overridden && line.unitPrice !== null) {
      showConfirm({
        title: t('orders:so.reresolve.confirmTitle'),
        content: t('orders:so.reresolve.confirmBody', {
          price: line.unitPrice.toFixed(2),
          currency,
          reason: line.overrideReason ?? '',
          qty: line.qty,
        }),
        okText: t('orders:so.reresolve.confirmOk'),
        cancelText: t('common:actions.cancel'),
        danger: false,
        onOk: () => onReResolveAtQty(line.key),
      });
      return;
    }
    onReResolveAtQty(line.key);
  };

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  // Picker options — searchable by SKU / names / Internal Product ID / the
  // selected customer's PN cross-refs (internal SKU OR customer PN entry).
  const options = useMemo<PickerOption[]>(
    () =>
      products.map((product) => {
        const pn =
          customerId != null
            ? product.customerPartNumbers?.find((row) => row.customerId === customerId)
            : undefined;
        return {
          value: product.id,
          label: `${product.sku} — ${product.displayName || product.name}`,
          partNumber: pn?.partNumber,
          searchText: [
            product.sku,
            product.displayName,
            product.name,
            product.internalProductId,
            pn?.partNumber,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        };
      }),
    [products, customerId]
  );

  const columns: TableProps<DraftSoLine>['columns'] = [
    {
      title: t('orders:fields.product'),
      dataIndex: 'productId',
      key: 'product',
      width: 320,
      render: (_: unknown, line) => (
        <Select<string, PickerOption>
          value={line.productId ?? undefined}
          onChange={(productId) => {
            const product = productById.get(productId);
            if (product) onSelectProduct(line.key, product);
          }}
          options={options}
          optionRender={(option) => (
            <div className="tw-flex tw-flex-col">
              <span>{option.data.label}</span>
              {option.data.partNumber && (
                <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                  {t('orders:so.lines.customerPn')}: {option.data.partNumber}
                </span>
              )}
            </div>
          )}
          showSearch
          filterOption={(input, option) =>
            (option?.searchText ?? '').includes(input.trim().toLowerCase())
          }
          loading={productsLoading}
          placeholder={t('orders:so.lines.productPlaceholder')}
          notFoundContent={
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('orders:so.lines.readyGateHint')}
            </Text>
          }
          size="large"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('orders:so.lines.customerPn'),
      dataIndex: 'customerPartNumber',
      key: 'customerPartNumber',
      width: 140,
      render: (value: string | null) => (value ? <PurpleTag>{value}</PurpleTag> : renderDash()),
    },
    {
      title: t('orders:fields.qty'),
      dataIndex: 'qty',
      key: 'qty',
      width: 110,
      render: (_: unknown, line) => (
        <InputNumber
          value={line.qty}
          min={1}
          precision={0}
          size="large"
          style={{ width: '100%' }}
          onChange={(value) => {
            if (value !== null) onQtyChange(line.key, value);
          }}
          aria-label={t('orders:fields.qty')}
        />
      ),
    },
    {
      title: t('orders:fields.unitPrice'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 170,
      render: (_: unknown, line) => {
        if (!line.productId) return renderDash();
        if (line.resolving) return <Spin size="small" />;
        if (!line.resolution) return renderDash();
        return (
          <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start">
            <PriceTraceTrigger
              resolution={line.resolution}
              unitPrice={line.unitPrice}
              currency={currency}
              overrideReason={line.overrideReason}
              marginFloorPct={minimumMarginPct}
              documentCurrency={currency}
            />
            {draftLineBeyondThreshold(line) && (
              <Tag color="orange" style={{ margin: 0 }}>
                {t('orders:so.priceStatus.pending_approval')}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t('orders:so.lines.lineTotal'),
      key: 'lineTotal',
      width: 130,
      align: 'right',
      render: (_: unknown, line) => {
        const total = draftLineTotal(line);
        return total !== null ? (
          <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(total)}</Text>
        ) : (
          renderDash()
        );
      },
    },
    {
      title: t('orders:so.lines.margin'),
      key: 'margin',
      width: 150,
      align: 'right',
      render: (_: unknown, line) => {
        const margin = draftLineMargin(line, currency);
        if (!margin) {
          // A price in one currency against a cost in another has no margin —
          // say so instead of printing a dash with no reason.
          const costCurrency = draftLineCostCurrency(line);
          if (costCurrency && costCurrency !== currency && draftLineLandedCost(line) !== null) {
            return (
              <DismissibleTooltip
                title={t('orders:so.lines.marginNotComparable', {
                  orderCurrency: currency,
                  costCurrency,
                })}
              >
                <span tabIndex={0}>{renderDash()}</span>
              </DismissibleTooltip>
            );
          }
          return renderDash();
        }
        // Minimum margin (2026-09-15): the tag carries the words, the colour
        // is secondary — never a colour-only signal. Markup on cost rides
        // the tooltip so nobody reads the floor as a markup.
        const floor = draftLineMarginFloor(line, minimumMarginPct, currency);
        const below = floor.status === 'below_floor';
        const params = {
          margin: formatPct(margin.pct),
          markup: floor.markupPct === null ? '—' : formatPct(floor.markupPct),
          floor: formatPct(floor.floorPct),
        };
        const tooltip =
          floor.floorPct === null
            ? t('orders:so.lines.marginTooltipNoFloor', params)
            : below
              ? t('orders:so.lines.belowFloorTooltip', params)
              : t('orders:so.lines.marginTooltip', params);
        return (
          <DismissibleTooltip title={tooltip}>
            <div
              className="tw-flex tw-flex-col tw-gap-1 tw-items-end"
              tabIndex={0}
              role="note"
              aria-label={tooltip}
            >
              <Text
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  color: margin.amount >= 0 ? token.colorSuccess : token.colorError,
                }}
              >
                {formatAmount(margin.amount)} ({formatAmount(margin.pct)}%)
              </Text>
              {below && (
                <Tag color="orange" style={{ margin: 0 }}>
                  {t('orders:so.lines.belowFloor')}
                </Tag>
              )}
            </div>
          </DismissibleTooltip>
        );
      },
    },
    {
      title: t('orders:so.lines.action'),
      key: 'action',
      // 96px: the S3.3 re-resolve icon seat sits beside the More dropdown.
      width: 96,
      fixed: 'right',
      render: (_: unknown, line) => (
        <div className="tw-flex tw-items-center">
          {/* S3.3 TRIPLE-gate (DA-6/DA-7, RULING-4): the SO pages ride
              sales.orders, which the demo profile enables — the seat must
              vanish when engines.pricing is off (showDisabled false ⇒ renders
              nothing) AND when the actor lacks finance.costBasis.edit
              (hide, never disable). */}
          {reResolveSeat && (
            <FeatureGate flag="engines.pricing" showDisabled={false}>
              {line.productId && !line.resolving ? (
                <Tooltip title={t('orders:so.reresolve.action')}>
                  <Button
                    type="text"
                    aria-label={t('orders:so.reresolve.actionFor', { name: line.sku || line.key })}
                    icon={<SyncOutlined />}
                    onClick={() => handleReResolveClick(line)}
                  />
                </Tooltip>
              ) : null}
            </FeatureGate>
          )}
          <Dropdown
            menu={{
              items: [
                ...(line.productId && !line.resolving
                  ? [
                      {
                        key: 'override',
                        label: t('orders:so.override.action'),
                        icon: <EditOutlined />,
                        onClick: () => setOverrideKey(line.key),
                      },
                    ]
                  : []),
                ...(line.overridden
                  ? [
                      {
                        key: 'restore',
                        label: t('orders:so.override.restore'),
                        icon: <UndoOutlined />,
                        onClick: () => onRestorePrice(line.key),
                      },
                    ]
                  : []),
                { type: 'divider' as const },
                {
                  key: 'remove',
                  label: t('orders:so.lines.remove'),
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => onRemoveLine(line.key),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              aria-label={t('orders:so.lines.actionsFor', { name: line.sku || line.key })}
              icon={<MoreOutlined />}
            />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <TableContainer>
        <Table<DraftSoLine>
          columns={columns}
          dataSource={lines}
          rowKey="key"
          pagination={false}
          locale={{
            emptyText: (
              <Text type="secondary">{t('orders:so.lines.empty')}</Text>
            ),
          }}
          scroll={{ x: 1120 }}
        />
      </TableContainer>

      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        onClick={onAddLine}
        size="large"
        style={{
          marginTop: token.marginMD,
          height: `${controlHeightTokens.controlHeightXL}px`,
          borderRadius: radiusTokens.card,
        }}
      >
        {t('orders:so.lines.add')}
      </Button>

      <PriceOverrideModal
        open={overrideKey !== null}
        productLabel={
          overrideLine ? `${overrideLine.sku} — ${overrideLine.productName}` : ''
        }
        currency={currency}
        resolvedUnitPrice={overrideLine?.resolution?.unitPrice ?? null}
        currentUnitPrice={overrideLine?.unitPrice ?? null}
        onCancel={() => setOverrideKey(null)}
        onApply={(unitPrice, reason) => {
          if (overrideKey) onApplyOverride(overrideKey, unitPrice, reason);
          setOverrideKey(null);
        }}
      />
    </>
  );
};

export default SoLineEditor;
